import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { restaurantScopedStorage } from '@/shared/store/restaurantScopedStorage'
import { WebsiteDocument } from '../schema/document'
import { followRestaurantMap } from '../utils/documentOps'
import {
  PublishIssue,
  publishableDocumentSchema,
  toPublishIssue
} from '../schema/publish'
import { DOCUMENT_VERSION } from '../config'
import starter from '../fixtures/demo-site.json'

/** What the properties panel is currently talking about. */
export type Selection =
  | { kind: 'row'; id: string }
  | { kind: 'column'; id: string }
  | { kind: 'element'; id: string }
  | null

export type SaveState = 'saved' | 'saving' | 'dirty'

/** The two ways of working on the page: two sidebar entries, two routes. */
export type EditorMode = 'template' | 'builder'

interface WebsiteState {
  draft: WebsiteDocument
  /** What guests see. Null until the first publication. */
  published: WebsiteDocument | null

  activePageId: string
  editingLanguage: string
  selection: Selection
  saveState: SaveState
  publishErrors: PublishIssue[]
  /**
   * Whether one of the editor's dialogs is open over the canvas.
   *
   * The overlay scrollbars are `position: absolute` at a z-index of 30 and this
   * app's modals sit at 5 and 6, so a bar left on screen paints a bright white
   * line straight down the dimmed backdrop. Neither the canvas nor the panel
   * can be scrolled behind a dialog, so they take their bars away instead of
   * entering a z-index race that would change how every other dialog in the app
   * covers the sidebar.
   */
  dialogOpen: boolean
  /**
   * Whether the builder has touched the document since the template last built
   * it. Saving from the template replaces the page either way; this is what
   * decides whether it asks first, because only then is there work to lose.
   */
  customized: boolean

  /*
    Undo is a stack of whole documents rather than of inverse operations.
    Documents here are a few kilobytes of plain JSON and every edit already
    produces a new one, so keeping fifty of them costs less than getting the
    inverse of "change layout, which moved three paragraphs" right.
  */
  past: WebsiteDocument[]
  future: WebsiteDocument[]
  /**
   * The document as it was before the current burst of previews began, or null
   * when no burst is open. A colour picker streams a value every 150ms while
   * the pointer is down; without this, one colour decision would be fifty
   * undo steps and the rest of the stack would be gone.
   */
  historyBaseline: WebsiteDocument | null

  apply: (change: (doc: WebsiteDocument) => WebsiteDocument) => void
  /** Writes without recording history. Ends with `commitPreview`. */
  previewApply: (change: (doc: WebsiteDocument) => WebsiteDocument) => void
  /** Closes a burst of previews, leaving exactly one entry behind it. */
  commitPreview: () => void
  /**
   * Points every map at the venue's connected place (`followRestaurantMap`).
   *
   * Outside the history and without marking the page `customized`: the owner
   * did not edit anything, the venue's connection changed. Undo past it brings
   * back the old address for a moment, and the next sync puts it right.
   */
  syncRestaurantMap: (embedUrl: string) => void
  select: (selection: Selection) => void
  setDialogOpen: (open: boolean) => void
  setEditingLanguage: (language: string) => void
  setActivePage: (pageId: string) => void
  setSaveState: (state: SaveState) => void
  /** Replaces the page with one the template built. Clears `customized`. */
  buildFromTemplate: (doc: WebsiteDocument) => void
  undo: () => void
  redo: () => void
  publish: () => boolean
  importDocument: (doc: WebsiteDocument) => void
}

/** The brief asks for at least fifty steps. */
const HISTORY_LIMIT = 50

/*
  A new site never starts empty (§6 of the brief): the demo document is the
  starter template until phase 2 brings the three real ones. It is the same file
  the prerender uses, so anything that renders here renders there.
*/
const STARTER = starter as unknown as WebsiteDocument

export const useWebsiteStore = create<WebsiteState>()(
  persist(
    (set, get) => ({
      draft: STARTER,
      published: null,
      activePageId: STARTER.pages[0].id,
      editingLanguage: STARTER.defaultLanguage,
      selection: null,
      saveState: 'saved',
      publishErrors: [],
      dialogOpen: false,
      customized: false,
      past: [],
      future: [],
      historyBaseline: null,

      apply: change =>
        set(state => {
          const next = change(state.draft)
          if (next === state.draft) return state
          return {
            draft: next,
            /*
              The baseline when a burst of previews was left open, which is what
              an edit made without releasing the picker looks like. Pushing it
              rather than the current draft keeps the drag and this edit as one
              step instead of stranding the drag outside the history.
            */
            past: [...state.past, state.historyBaseline ?? state.draft].slice(
              -HISTORY_LIMIT
            ),
            // Any new edit abandons the redo branch, which is what every editor
            // does and what people expect.
            future: [],
            historyBaseline: null,
            saveState: 'dirty',
            customized: true
          }
        }),

      previewApply: change =>
        set(state => {
          const next = change(state.draft)
          if (next === state.draft) return state
          return {
            draft: next,
            historyBaseline: state.historyBaseline ?? state.draft,
            saveState: 'dirty',
            customized: true
          }
        }),

      commitPreview: () =>
        set(state => {
          // Nothing moved: a click that opened and closed the picker.
          if (!state.historyBaseline || state.historyBaseline === state.draft) {
            return { historyBaseline: null }
          }
          return {
            past: [...state.past, state.historyBaseline].slice(-HISTORY_LIMIT),
            future: [],
            historyBaseline: null
          }
        }),

      syncRestaurantMap: embedUrl =>
        set(state => {
          const next = followRestaurantMap(state.draft, embedUrl)
          if (next === state.draft) return state
          return { draft: next, saveState: 'dirty' }
        }),

      select: selection => set({ selection }),
      setDialogOpen: dialogOpen => set({ dialogOpen }),
      setEditingLanguage: language => set({ editingLanguage: language }),
      setActivePage: pageId => set({ activePageId: pageId, selection: null }),
      setSaveState: saveState => set({ saveState }),
      buildFromTemplate: doc =>
        set(state => ({
          draft: doc,
          activePageId: doc.pages[0].id,
          past: [...state.past, state.draft].slice(-HISTORY_LIMIT),
          future: [],
          selection: null,
          historyBaseline: null,
          saveState: 'dirty',
          customized: false
        })),

      undo: () =>
        set(state => {
          if (state.past.length === 0) return state
          const previous = state.past[state.past.length - 1]
          return {
            draft: previous,
            past: state.past.slice(0, -1),
            future: [state.draft, ...state.future].slice(0, HISTORY_LIMIT),
            selection: null,
            historyBaseline: null,
            saveState: 'dirty'
          }
        }),

      redo: () =>
        set(state => {
          if (state.future.length === 0) return state
          const [next, ...rest] = state.future
          return {
            draft: next,
            past: [...state.past, state.draft].slice(-HISTORY_LIMIT),
            future: rest,
            selection: null,
            historyBaseline: null,
            saveState: 'dirty'
          }
        }),

      publish: () => {
        const { draft } = get()
        const result = publishableDocumentSchema.safeParse(draft)
        if (!result.success) {
          set({
            publishErrors: result.error.issues.map(toPublishIssue)
          })
          return false
        }
        set({ published: draft, publishErrors: [] })
        return true
      },

      importDocument: doc =>
        set({
          draft: doc,
          activePageId: doc.pages[0].id,
          editingLanguage: doc.defaultLanguage,
          selection: null,
          past: [],
          future: [],
          historyBaseline: null,
          saveState: 'saved'
        })
    }),
    {
      name: 'website-builder-storage',
      storage: createJSONStorage(() => restaurantScopedStorage),
      version: DOCUMENT_VERSION,
      /*
        History is not persisted. Undo is a session's worth of context, and
        restoring somebody's fifty-step stack from last Tuesday would let them
        undo their way back past changes they have since published.
      */
      partialize: state => ({
        draft: state.draft,
        published: state.published,
        activePageId: state.activePageId,
        editingLanguage: state.editingLanguage,
        customized: state.customized
      })
    }
  )
)
